/**
 * Maps a stepData screen code (the screen on which a draft document was uploaded)
 * to the canonical {@link DocumentCategory}.
 *
 * Used only for DRAFT claims, where the BFF cannot infer the category from NOVA
 * (the export is not produced for drafts). For SUBMITTED claims, the BFF derives
 * `category` from the NOVA `Submitted_File.Category` form_field — the frontend
 * consumes it directly.
 *
 * The actual screen codes in this codebase (see `src/config/screenMapping.ts`) are:
 *   - `04-01-FACTURES`    : InvoiceUploadScreen        — common to all flows
 *   - `04-03-JUSTIFICATIFS`: SupportingDocumentsScreen — common to all flows
 *   - `04E-01-FACTURES`   : AppendInvoiceUploadScreen  — append-expenses flow
 *   - `04E-03-JUSTIFICATIFS`: AppendSupportingDocumentsScreen — append-expenses flow
 *   - `05-01-PAIEMENT`    : BankingInfoScreen          — void cheque
 *
 * The DEV-6896 plan listed flow-specific variants (e.g. `04M-01-FACTURES`,
 * `04A-01-FACTURES`, `04B-01-FACTURES`, `03M-04-FACTURES`, `05M-01-PAIEMENT`).
 * Those screen codes do NOT exist in this codebase — flows share the same upload
 * screens. Only the codes actually present in `screenMapping.ts` are mapped here.
 *
 * Unknown screen codes default to {@link DocumentCategory} `'OTHER'` (no exception).
 */
import type { DocumentCategory } from '../types';

const SCREEN_CODE_TO_CATEGORY: Record<string, DocumentCategory> = {
  // Invoices
  '04-01-FACTURES': 'INVOICE',
  '04E-01-FACTURES': 'INVOICE',

  // Void cheque (banking info — specimen)
  '05-01-PAIEMENT': 'VOID_CHEQUE',

  // Supporting documents
  '04-03-JUSTIFICATIFS': 'SUPPORTING_DOCUMENT',
  '04E-03-JUSTIFICATIFS': 'SUPPORTING_DOCUMENT',
};

/**
 * Resolves the {@link DocumentCategory} for a draft document uploaded under the
 * given screen code. Returns `'OTHER'` for any unmapped screen code.
 */
export function categoryFromScreenCode(screenCode: string): DocumentCategory {
  return SCREEN_CODE_TO_CATEGORY[screenCode] ?? 'OTHER';
}
